import React, { useState } from 'react';

import { useTheme } from '@mui/material';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import ColumnOpsDrawer from './ColumnOpsDrawer';
import { moveItemToOtherList, reorderItems } from './helpers';
import KanbanColumnArea from './KanbanColumnArea';
import ProvinceOpsDrawer from './ProvinceOpsDrawer';
import {
  KanbanColumn,
  KanbanItem,
  KdKanban,
  Objective,
} from '../../@types/State';

//https://egghead.io/lessons/react-reorder-columns-with-react-beautiful-dnd

interface Props {
  state: KdKanban;
  setState: (state: KdKanban) => void;
  isWarPlan: boolean;
}

const Kanban: React.FC<Props> = ({ state, setState, isWarPlan }) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<KanbanItem | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<KanbanColumn | null>(
    null
  );
  const [provinceDrawerOpen, setProvinceDrawerOpen] = useState(false);
  const [columnDrawerOpen, setColumnDrawerOpen] = useState(false);

  const toggleProvinceDrawer = () => {
    setProvinceDrawerOpen(!provinceDrawerOpen);
  };

  const toggleColumnDrawer = () => {
    setColumnDrawerOpen(!columnDrawerOpen);
    if (selectedColumn) {
      setSelectedColumn(null);
    }
  };
  const openColumnDrawer = (el: KanbanColumn) => {
    setSelectedColumn(el);
    setColumnDrawerOpen(true);
  };

  const handleColumnChange = (e: any, i: number) => {
    const newState = { ...state };
    const col = newState.columns[i];
    col.title = e.target.value;
    setState(newState);
  };

  const handleItemChange = (e: any, colIndex: number, itemInd: number) => {
    const newState = { ...state };
    const col = newState.columns[colIndex];
    const item = col.items[itemInd];
    item.content = e.target.value;
    setState(newState);
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    const srcColIndex = +source.droppableId;
    const destColIndex = +destination.droppableId;

    if (srcColIndex === destColIndex) {
      const newItems = reorderItems(
        state.columns[srcColIndex].items,
        source.index,
        destination.index
      );
      const newState = { ...state };
      newState.columns[srcColIndex].items = newItems;
      setState(newState);
    } else {
      const result = moveItemToOtherList(
        state.columns[srcColIndex],
        state.columns[destColIndex],
        source,
        destination
      );
      const newState = { ...state };
      newState.columns[srcColIndex].items = result[srcColIndex].items;
      newState.columns[destColIndex].items = result[destColIndex].items;
      setState(newState);
    }
  };

  const addOp = (op: string, selectedItem: KanbanItem | null) => {
    if (!selectedItem) return;
    if (!selectedItem.province.requiredOps) {
      selectedItem.province.requiredOps = [];
      selectedItem.province.requiredOps.push(op);
    } else if (selectedItem.province.requiredOps.indexOf(op) < 0) {
      selectedItem.province.requiredOps.push(op);
    }
    setState({ ...state });
  };

  const deleteOp = (op: string, selectedItem: KanbanItem | null) => {
    if (!selectedItem || !selectedItem.province.requiredOps) return;
    const index = selectedItem.province.requiredOps.indexOf(op);
    if (index > -1) {
      selectedItem.province.requiredOps.splice(index, 1);
    }
    setState({ ...state });
  };

  const addColumnOp = (op: string) => {
    if (!selectedColumn) return;
    if (!selectedColumn.requiredOps) {
      selectedColumn.requiredOps = [];
      selectedColumn.requiredOps.push(op);
      selectedColumn.items.forEach((item: KanbanItem) => addOp(op, item));
    } else if (selectedColumn.requiredOps.indexOf(op) < 0) {
      selectedColumn.requiredOps.push(op);
      selectedColumn.items.forEach((item: KanbanItem) => addOp(op, item));
    }
    setState({ ...state });
  };

  const deleteColumnOp = (op: string, column: KanbanColumn) => {
    if (!column || !column.requiredOps) return;
    const index = column.requiredOps.indexOf(op);
    if (index > -1) {
      column.requiredOps.splice(index, 1);
    }
    column.items.forEach((item: KanbanItem) => deleteOp(op, item));
    setState({ ...state });
  };

  const deleteObjective = (op: Objective) => {
    if (!selected || !selected.province.requiredObjectives) return;
    const index = selected.province.requiredObjectives.indexOf(op);
    if (index > -1) {
      selected.province.requiredObjectives.splice(index, 1);
    }
    setState({ ...state });
  };

  const deleteCol = (colInd: number) => {
    const newState = { ...state };
    newState.columns.splice(colInd, 1);
    setState(newState);
  };

  const addObjective = (objective: Objective) => {
    if (!selected) return;
    if (!selected.province.requiredObjectives) {
      selected.province.requiredObjectives = [];
      selected.province.requiredObjectives.push(objective);
    } else if (selected.province.requiredObjectives.indexOf(objective) < 0) {
      selected.province.requiredObjectives.push(objective);
    }
    setState({ ...state });
  };

  const handleCollapse =
    (item: KanbanItem) => (e: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        setSelected(item);
      } else {
        setSelected(null);
      }
    };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        {state.columns.map((el: KanbanColumn, colInd: number) => (
          <Droppable key={colInd} droppableId={`${colInd}`}>
            {(provided, snapshot) => (
              <KanbanColumnArea
                column={el}
                colInd={colInd}
                theme={theme}
                selected={selected}
                handleCollapse={handleCollapse}
                handleItemChange={handleItemChange}
                deleteOp={deleteOp}
                deleteObjective={deleteObjective}
                deleteColumnOp={deleteColumnOp}
                toggleProvinceDrawer={toggleProvinceDrawer}
                openColumnDrawer={openColumnDrawer}
                handleColumnChange={handleColumnChange}
                provided={provided}
                snapshot={snapshot}
                isWarPlan={isWarPlan}
                deleteCol={deleteCol}
              />
            )}
          </Droppable>
        ))}
      </DragDropContext>
      <ProvinceOpsDrawer
        drawerOpen={provinceDrawerOpen}
        toggleDrawer={toggleProvinceDrawer}
        isWarPlan={isWarPlan}
        addOp={addOp}
        addObjective={addObjective}
        selected={selected}
      />
      <ColumnOpsDrawer
        drawerOpen={!!selectedColumn}
        toggleColumnDrawer={toggleColumnDrawer}
        addColumnOp={addColumnOp}
        selectedColumn={selectedColumn}
      />
    </>
  );
};

export default Kanban;
